import type { RespostaPublica } from "../types";

/** Uma mensagem como ela está NA TELA. `editadoEm` é o que foi pintado (""=nunca editada). */
export interface MensagemNaTela {
  id: string;
  editadoEm: string;
}

/** Onde uma mensagem nova entra: antes desta que já está na tela, ou no fim (`null`). */
export interface Entrada {
  resposta: RespostaPublica;
  antesDe: string | null;
}

export interface PlanoDaThread {
  /** Chegaram depois — cada uma com a âncora que mantém a ordem do servidor. */
  anexar: Entrada[];
  /** Ids que saíram: o time tornou o comentário interno de novo, ou o excluiu. */
  remover: string[];
  /** Mesmo id, texto editado: a bolha é repintada no lugar. */
  trocar: RespostaPublica[];
}

/**
 * O que fazer com a thread que JÁ está na tela para ela virar `payload` — sem repintar o
 * que não mudou (DEV-210).
 *
 * Existe porque o widget não tem React: a tela é string concatenada dentro de um Shadow
 * DOM, e repintá-la arrancaria o rascunho, o foco e a rolagem de quem está escrevendo. Era
 * por isso que o poll de fundo só mexia na badge — e, com o chamado aberto, a resposta do
 * time não aparecia até fechar e reabrir. Aqui sai o PLANO (puro, testável); quem toca o
 * DOM é o widget.
 *
 * Três operações, e nenhuma é luxo:
 *  - **anexar** é o caso do card: chegou resposta nova;
 *  - **remover** porque o time pode voltar um comentário para interno (`visibilidade`), e
 *    uma frase retirada não pode continuar na tela do cliente;
 *  - **trocar** porque comentário é editável, e texto velho na tela é informação errada.
 *
 * A **âncora** do anexar não é preciosismo: um comentário ANTIGO que vira público entra no
 * meio da thread (o servidor ordena por `criado_em`), e jogá-lo no fim mostraria os
 * horários fora de ordem. A âncora é a próxima mensagem que já está na tela — e quem sai
 * no `remover` não serve de âncora, senão a inserção miraria um nó que vai deixar de existir.
 */
export function planejarThread(naTela: MensagemNaTela[], payload: RespostaPublica[]): PlanoDaThread {
  const antes = new Map(naTela.map((m) => [m.id, m]));
  const noPayload = new Set(payload.map((r) => r.id));
  // Só quem está na tela E continua no payload pode ancorar uma inserção.
  const sobrevive = new Set(naTela.filter((m) => noPayload.has(m.id)).map((m) => m.id));

  const anexar: Entrada[] = [];
  const trocar: RespostaPublica[] = [];

  payload.forEach((r, i) => {
    const atual = antes.get(r.id);
    if (atual === undefined) {
      const seguinte = payload.slice(i + 1).find((p) => sobrevive.has(p.id));
      anexar.push({ resposta: r, antesDe: seguinte?.id ?? null });

      return;
    }
    if (atual.editadoEm !== (r.editadoEm ?? "")) trocar.push(r);
  });

  return {
    anexar,
    remover: naTela.filter((m) => !noPayload.has(m.id)).map((m) => m.id),
    trocar,
  };
}
